enum Roles {
    Superuser = "Superuser",
    BetaTester = "BetaTester",
    Admin = "Admin",
    SuperAdmin = "SuperAdmin",
    Instructor = "Instructor",
    Assistant = "Assistant",
    Staff = "Staff",
    AreaManager = "AreaManager",
    EndUser = "EndUser",
}

// Rollen-Hierarchie: höhere Rollen implizieren niedrigere. Einzige Quelle für
// Server-AuthChecker und Ownership-Guards (Nicht-Hierarchie-Rollen wie
// BetaTester/Assistant stehen bewusst nicht drin).
// AreaManager steht seit den Sichtbarkeitsregeln (#706, 2026-09) UNTER Admin: ein
// Bereichsleiter ist ein Mitarbeitenden-Zugang mit Standort-Scope, kein Admin.
// Wer Admin-Rechte braucht, bekommt die Admin-Rolle zusätzlich.
export const roleHierarchy: Array<Roles> = [
    Roles.Superuser,
    Roles.SuperAdmin,
    Roles.Admin,
    Roles.AreaManager,
    Roles.Instructor,
    Roles.Staff,
    Roles.EndUser,
];

// App-Rollen, die ein bare @Authorized() (ohne Rollenargumente) erreichen darf:
// alle Mitarbeiter-/Backend-Rollen, aber NICHT der Endkunde. Endkunden-Zugriff
// muss immer explizit über Roles.EndUser bzw. APP_ROLES_INCL_END_USER freigegeben
// werden — so ist jeder EUA-erreichbare Resolver bewusst gelistet (statt implizit
// über bare @Authorized() erreichbar, was tenant-weite IDOR-Lücken begünstigt hat).
export const APP_ROLES: Array<Roles> = [
    Roles.Admin,
    Roles.Superuser,
    Roles.SuperAdmin,
    Roles.BetaTester,
    Roles.Staff,
    Roles.Instructor,
    Roles.Assistant,
    Roles.AreaManager,
];

// Zusätzlich der Endkunde — für Resolver, die bewusst auch die End-User-App
// bedienen und dort für reine EndUser erreichbar sein müssen.
export const APP_ROLES_INCL_END_USER: Array<Roles> = [...APP_ROLES, Roles.EndUser];

// Backoffice-/Mitarbeiter-Rollen für sensible bzw. PII-tragende Felder und Resolver,
// deren Threat-Model Endkunden UND anonyme Aufrufer ausschließt (z.B. Instructor-
// Login-Mail, Location-Kontaktdaten). Bewusst EIGENSTÄNDIG von APP_ROLES definiert
// (auch wenn die Menge aktuell übereinstimmt): eine künftige Änderung am bare-
// @Authorized()-Gate darf diese PII-Felder nicht ungewollt öffnen. Enthält garantiert
// KEIN Roles.EndUser — per Invarianz-Test in customAuthChecker.test.ts abgesichert.
export const BACKOFFICE_ROLES: Array<Roles> = [
    Roles.Admin,
    Roles.Superuser,
    Roles.SuperAdmin,
    Roles.BetaTester,
    Roles.Staff,
    Roles.Instructor,
    Roles.Assistant,
    Roles.AreaManager,
];

// True, wenn mindestens eine Rolle in der Hierarchie auf oder ueber `required` liegt.
// Nicht-Hierarchie-Rollen (BetaTester, Assistant) zaehlen nie. Gedacht fuer
// Guards ausserhalb von @Authorized (FieldResolver, Service-Checks).
export const hasRoleAtLeast = (roles: ReadonlyArray<string> | null | undefined, required: Roles): boolean => {
    const requiredIndex = roleHierarchy.indexOf(required);

    return (roles ?? []).some((role) => {
        const index = roleHierarchy.indexOf(role as Roles);

        return index !== -1 && index <= requiredIndex;
    });
};

// Ein "Mitarbeitenden-Zugang" ist ein Login mit mindestens einer App-Rolle
// (Instructor, Assistant, Staff, AreaManager, BetaTester) und KEINER Rolle ab Admin.
// Negativ definiert, damit auch Kombinationen wie BetaTester-only oder Assistant-only
// eingeschraenkt sind. Reine Endkunden (nur EndUser) sind KEIN Mitarbeitenden-Zugang.
//
// Einzige Quelle der Wahrheit fuer Server UND Client (#809): der Server-Guard
// `isOnlyStaffAccess` (src/server/auth/staffAccessGuard.ts) und der Client-Hook
// `useIsStaffOnlyAccess` (src/client/helpers/useIsStaffOnlyAccess.ts) delegieren beide
// hierher. Vorher hatte der Client eine eigene, engere Definition
// (`Instructor || AreaManager`) und bot Staff/Assistant/BetaTester Aktionen an, die der
// Server mit `ForbiddenError` ablehnt.
export const isStaffOnlyRoleSet = (roles: ReadonlyArray<string> | null | undefined): boolean => {
    const effectiveRoles = roles ?? [];
    const hasAppRole = APP_ROLES.some((role) => effectiveRoles.includes(role));

    return hasAppRole && !hasRoleAtLeast(effectiveRoles, Roles.Admin);
};

export default Roles;
